//
//  navigation.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module defines navigation routing and navigation data.
//

import {
    type GroupIdentity,
    Permission,
    type PersonnelIdentity,
    type TrialIdentity,
    type TrialInstitutionIdentity,
    type TrialInstitutionSupportingSiteIdentity,
    type InstitutionIdentity,
    type DepartmentIdentity,
    type PatientIdentity,
    type TrialFormIdentity,
    type Role
} from "src/api/Tmd.dtos";
import AppSettings from "src/AppSettings";

import {
    LinkPermissionMode,
    PermissionScopeMode,
    type ILayoutLink,
    type ILayoutSubMenu
} from "../contexts/layouts/LayoutContext";
import openSearch from "./searchLauncher";
import { settingsNavigationPermissions } from "./settingsNavigation";
import { trialConfigurationPermissions } from "./trialConfigurationNavigation";
import { trialItemsPermissions } from "./trialItemsNavigation";

/**
 * Defines ordered top-level destinations and their client permission requirements.
 *
 * Assumptions: Consumers filter the shared list; construction does not authorize access.
 */
export const mainNavigationItems: ILayoutLink[] = [
    {
        icon: AppSettings.icons.dashboard,
        label: "Dashboard",
        href: "/dashboard",
        permissionMode: LinkPermissionMode.Any,
        permissionScopeMode: PermissionScopeMode.Any,
        permissions: [Permission.DashboardView]
    },
    {
        icon: AppSettings.icons.models.trial,
        label: "Trials",
        href: "/trials",
        permissionMode: LinkPermissionMode.Any,
        permissionScopeMode: PermissionScopeMode.Any,
        permissions: [Permission.TrialView, Permission.TrialUpdate]
    },
    {
        icon: AppSettings.icons.models.institution,
        label: "Institutions",
        href: "/institutions",
        permissionMode: LinkPermissionMode.Any,
        permissions: [Permission.InstitutionView]
    },
    {
        icon: AppSettings.icons.models.personnel,
        label: "Personnel",
        href: "/personnel",
        permissionMode: LinkPermissionMode.Any,
        permissions: [Permission.PersonnelView]
    },
    {
        icon: AppSettings.icons.models.group,
        label: "Groups",
        href: "/groups",
        permissionMode: LinkPermissionMode.Any,
        permissions: [Permission.GroupView]
    },
    {
        icon: AppSettings.icons.settings,
        label: "Settings",
        href: "/settings",
        permissionMode: LinkPermissionMode.Any,
        permissions: settingsNavigationPermissions
    },
    {
        icon: AppSettings.icons.search,
        href: "/search",
        actionFn: openSearch,
        style: {
            marginLeft: "auto"
        },
        permissionMode: LinkPermissionMode.Any,
        permissions: [
            Permission.TrialView,
            Permission.TrialUpdate,
            Permission.PersonnelView,
            Permission.InstitutionView
        ]
    }
];

/**
 * Builds trial navigation and its trial permission context.
 *
 * Postconditions: Missing identity returns undefined; otherwise routes and titles use the identity
 * as supplied, with no ID validation or permission filtering.
 */
export const createTrialSubMenu = (trialIdentity?: TrialIdentity): ILayoutSubMenu | undefined => {
    if (!trialIdentity) {
        return undefined;
    }

    return {
        code: "trial",
        title: `${trialIdentity.displayNumber} ${trialIdentity.displayTitle}`,
        context: {
            trialId: trialIdentity.id
        },
        items: [
            {
                icon: undefined,
                label: "Details",
                href: `/trials/${trialIdentity.id}/details`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialDetailsView]
            },
            {
                icon: AppSettings.icons.models.trialMilestones,
                label: "Milestones",
                href: `/trials/${trialIdentity.id}/milestones`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialDetailsView]
            },
            {
                icon: AppSettings.icons.models.trialInstitution,
                label: "Institutions",
                href: `/trials/${trialIdentity.id}/institutions`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialInstitutionView]
            },
            {
                icon: AppSettings.icons.models.personnelRole,
                label: "Personnel",
                href: `/trials/${trialIdentity.id}/personnel`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialPersonnelRoleView]
            },
            {
                icon: AppSettings.icons.models.participant,
                label: "Participants",
                href: `/trials/${trialIdentity.id}/participants`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialPatientView]
            },
            {
                icon: AppSettings.icons.models.item,
                label: "Items",
                href: `/trials/${trialIdentity.id}/items`,
                permissionMode: LinkPermissionMode.Any,
                permissions: trialItemsPermissions
            },
            {
                icon: AppSettings.icons.models.trialForm,
                label: "Forms",
                href: `/trials/${trialIdentity.id}/forms`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialFormView]
            },
            {
                icon: AppSettings.icons.models.trialGroup,
                label: "Groups",
                href: `/trials/${trialIdentity.id}/groups`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialGroupView]
            },
            {
                icon: AppSettings.icons.models.trialTreatment,
                label: "Treatments",
                href: `/trials/${trialIdentity.id}/treatments`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialTreatmentsView]
            },
            {
                icon: AppSettings.icons.models.trialNote,
                label: "Notes",
                href: `/trials/${trialIdentity.id}/notes`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialNoteView]
            },
            {
                icon: AppSettings.icons.models.trialTracker,
                label: "QA Tracker",
                href: `/trials/${trialIdentity.id}/trackers`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialTrackerView]
            },
            ...(trialIdentity.qaProgramChecklistInitialized
                ? [
                      {
                          icon: AppSettings.icons.models.qaProgramChecklist,
                          label: "QA Program Checklist",
                          href: `/trials/${trialIdentity.id}/qa-program-checklist`,
                          permissionMode: LinkPermissionMode.Any,
                          permissions: [Permission.TrialQaProgramChecklistView]
                      }
                  ]
                : []),
            {
                icon: undefined,
                label: "Configuration",
                href: `/trials/${trialIdentity.id}/configuration`,
                permissionMode: LinkPermissionMode.Any,
                permissions: trialConfigurationPermissions
            }
        ]
    };
};

/**
 * Builds institution-within-trial navigation from both identities.
 *
 * Postconditions: Missing either identity returns undefined. Context combines the trial ID with
 * the trial-institution IDs; identity consistency is assumed rather than checked. Capabilities are
 * omitted unless the trial enables at least one, because the page would otherwise offer nothing to
 * measure coverage against; the route itself stays reachable.
 */
export const createTrialInstitutionSubMenu = (
    trialIdentity?: TrialIdentity,
    trialInstitutionIdentity?: TrialInstitutionIdentity
): ILayoutSubMenu | undefined => {
    if (!trialIdentity || !trialInstitutionIdentity) {
        return undefined;
    }

    return {
        code: "trialInstitution",
        title: trialInstitutionIdentity.institutionName,
        context: {
            trialId: trialIdentity.id,
            institutionId: trialInstitutionIdentity.institutionId,
            trialInstitutionId: trialInstitutionIdentity.id
        },
        items: [
            {
                icon: undefined,
                label: "Details",
                href: `/trials/${trialIdentity.id}/institutions/${trialInstitutionIdentity.id}/details`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialInstitutionDetailsView]
            },
            {
                icon: AppSettings.icons.models.trialInstitutionSupportingSite,
                label: "Supporting Sites",
                href: `/trials/${trialIdentity.id}/institutions/${trialInstitutionIdentity.id}/supporting-sites`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialInstitutionDetailsView]
            },
            ...(trialIdentity.capabilitiesEnabled
                ? [
                      {
                          icon: AppSettings.icons.models.trialInstitutionCapability,
                          label: "Capabilities",
                          href: `/trials/${trialIdentity.id}/institutions/${trialInstitutionIdentity.id}/capabilities`,
                          permissionMode: LinkPermissionMode.Any,
                          permissions: [Permission.TrialInstitutionDetailsView]
                      }
                  ]
                : []),
            {
                icon: AppSettings.icons.models.participant,
                label: "Participants",
                href: `/trials/${trialIdentity.id}/institutions/${trialInstitutionIdentity.id}/participants`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialPatientView]
            },
            {
                icon: AppSettings.icons.models.item,
                label: "Items",
                href: `/trials/${trialIdentity.id}/institutions/${trialInstitutionIdentity.id}/items`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialInstitutionItemInstanceView]
            },
            {
                icon: AppSettings.icons.models.personnelRole,
                label: "Personnel",
                href: `/trials/${trialIdentity.id}/institutions/${trialInstitutionIdentity.id}/personnel`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialPersonnelRoleView]
            },
            {
                icon: AppSettings.icons.models.trialInstitutionNote,
                label: "Notes",
                href: `/trials/${trialIdentity.id}/institutions/${trialInstitutionIdentity.id}/notes`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialNoteView]
            },
            {
                icon: AppSettings.icons.models.emailRecipientConfiguration,
                label: "Emails",
                href: `/trials/${trialIdentity.id}/institutions/${trialInstitutionIdentity.id}/emails`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialInstitutionEmailRecipientsView]
            }
        ]
    };
};

/**
 * Builds the supporting site sub-menu from the trial, parent and supporting site identities.
 *
 * Postconditions: Returns undefined until all three identities resolve. The permission context
 * carries the PARENT institution, because a supporting site and its personnel are authorized
 * against the parent trial institution rather than the supporting institution.
 */
export const createTrialInstitutionSupportingSiteSubMenu = (
    trialIdentity?: TrialIdentity,
    trialInstitutionIdentity?: TrialInstitutionIdentity,
    supportingSiteIdentity?: TrialInstitutionSupportingSiteIdentity
): ILayoutSubMenu | undefined => {
    if (!trialIdentity || !trialInstitutionIdentity || !supportingSiteIdentity) {
        return undefined;
    }

    const base =
        `/trials/${trialIdentity.id}/institutions/${trialInstitutionIdentity.id}` +
        `/supporting-sites/${supportingSiteIdentity.id}`;

    return {
        code: "trialInstitutionSupportingSite",
        title: supportingSiteIdentity.institutionName,
        context: {
            trialId: trialIdentity.id,
            institutionId: trialInstitutionIdentity.institutionId,
            trialInstitutionId: trialInstitutionIdentity.id
        },
        items: [
            {
                icon: undefined,
                label: "Details",
                href: `${base}/details`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialInstitutionDetailsView]
            },
            {
                icon: AppSettings.icons.models.personnelRole,
                label: "Personnel",
                href: `${base}/personnel`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialPersonnelRoleView]
            }
        ]
    };
};

/**
 * Builds participant navigation in trial or trial-institution scope.
 *
 * Postconditions: Trial and participant identities are required; the institution identity is optional.
 * When supplied, it selects the institution route and its IDs take precedence in the context.
 */
export const createPatientSubMenu = (
    trialIdentity?: TrialIdentity,
    trialInstitutionIdentity?: TrialInstitutionIdentity,
    patientIdentity?: PatientIdentity
): ILayoutSubMenu | undefined => {
    if (!trialIdentity || !patientIdentity) {
        return undefined;
    }

    const basePath = trialInstitutionIdentity
        ? `/trials/${trialIdentity.id}/institutions/${trialInstitutionIdentity.id}/participants/${patientIdentity.id}`
        : `/trials/${trialIdentity.id}/participants/${patientIdentity.id}`;

    return {
        code: "patient",
        title: patientIdentity.registrationNumber,
        context: {
            trialId: trialIdentity.id,
            institutionId: trialInstitutionIdentity?.institutionId ?? patientIdentity.institutionId,
            trialInstitutionId: trialInstitutionIdentity?.id ?? patientIdentity.trialInstitutionId,
            patientId: patientIdentity.id
        },
        items: [
            {
                icon: undefined,
                label: "Details",
                href: `${basePath}/details`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialPatientView]
            },
            {
                icon: AppSettings.icons.models.administrationCaseReport,
                label: "Administration Case Report",
                href: `${basePath}/administrationcasereport`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialFormView]
            },
            {
                icon: AppSettings.icons.models.patientItem,
                label: "Items",
                href: `${basePath}/items`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PatientItemView]
            },
            {
                icon: AppSettings.icons.models.emailRecipientConfiguration,
                label: "Emails",
                href: `${basePath}/emails`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialPatientEmailRecipientsView]
            }
        ]
    };
};

/** Builds form navigation only after the trial and form identities have loaded. */
export const createTrialFormSubMenu = (
    trialIdentity?: TrialIdentity,
    trialFormIdentity?: TrialFormIdentity
): ILayoutSubMenu | undefined => {
    if (!trialIdentity || !trialFormIdentity) {
        return undefined;
    }

    const base = `/trials/${trialIdentity.id}/forms/${trialFormIdentity.id}`;

    return {
        code: "trialForm",
        title: [trialFormIdentity.formTemplateName, trialFormIdentity.patientRegistrationNumber]
            .filter(Boolean)
            .join(" — "),
        context: {
            trialId: trialIdentity.id,
            institutionId: trialFormIdentity.institutionId,
            patientId: trialFormIdentity.patientId
        },
        items: [
            {
                icon: undefined,
                label: "Details",
                href: base,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialFormView]
            },
            {
                icon: AppSettings.icons.models.collectionInstance,
                label: "Data Entry",
                href: `${base}/entry`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialFormDataEntry]
            },
            {
                icon: AppSettings.icons.models.administrationCaseReport,
                label: "Administration Case Report",
                href: `${base}/administrationcasereport`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialFormView]
            },
            {
                icon: AppSettings.icons.models.trialFormReport,
                label: "QA Case Report",
                href: `${base}/report`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialFormView]
            },
            {
                icon: AppSettings.icons.models.emailRecipientConfiguration,
                label: "Emails",
                href: `${base}/emails`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialFormEmailRecipientsView]
            }
        ]
    };
};

/**
 * Builds institution navigation with its institution context, or undefined without an identity.
 */
export const createInstitutionSubMenu = (
    institutionIdentity?: InstitutionIdentity
): ILayoutSubMenu | undefined => {
    if (!institutionIdentity) {
        return undefined;
    }

    return {
        code: "institution",
        title: institutionIdentity.name,
        context: {
            institutionId: institutionIdentity.id
        },
        items: [
            {
                icon: undefined,
                label: "Details",
                href: `/institutions/${institutionIdentity.id}/details`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InstitutionDetailsView]
            },
            {
                icon: AppSettings.icons.models.department,
                label: "Departments",
                href: `/institutions/${institutionIdentity.id}/departments`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InstitutionDepartmentView]
            },
            {
                icon: AppSettings.icons.models.personnelRole,
                label: "Personnel",
                href: `/institutions/${institutionIdentity.id}/personnel`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InstitutionPersonnelView]
            },
            {
                icon: AppSettings.icons.models.institutionForm,
                label: "Forms",
                href: `/institutions/${institutionIdentity.id}/forms`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InstitutionFormView]
            },
            {
                icon: AppSettings.icons.models.emailRecipientConfiguration,
                label: "Emails",
                href: `/institutions/${institutionIdentity.id}/emails`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InstitutionEmailRecipientsView]
            },
            {
                icon: AppSettings.icons.models.institutionNote,
                label: "Notes",
                href: `/institutions/${institutionIdentity.id}/notes`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InstitutionNoteView]
            }
        ]
    };
};

/**
 * Builds department navigation when both institution and department identities are present.
 *
 * Postconditions: Routes and context use the department's institution ID; the separate institution
 * identity is a presence guard and is not checked for consistency.
 */
export const createDepartmentSubMenu = (
    institutionIdentity?: InstitutionIdentity,
    departmentIdentity?: DepartmentIdentity
): ILayoutSubMenu | undefined => {
    if (!institutionIdentity || !departmentIdentity) {
        return undefined;
    }

    return {
        code: "department",
        title: departmentIdentity.name,
        context: {
            institutionId: departmentIdentity.institutionId,
            departmentId: departmentIdentity.id
        },
        items: [
            {
                icon: undefined,
                label: "Details",
                href: `/institutions/${departmentIdentity.institutionId}/departments/${departmentIdentity.id}/details`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InstitutionDepartmentView]
            },
            {
                icon: AppSettings.icons.models.personnelRole,
                label: "Personnel",
                href: `/institutions/${departmentIdentity.institutionId}/departments/${departmentIdentity.id}/personnel`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.InstitutionDepartmentView]
            }
        ]
    };
};

/**
 * Builds personnel navigation and context, or undefined when the identity is absent.
 *
 * Postconditions: The supplied first and last names are joined without trimming.
 */
export const createPersonnelSubMenu = (
    personnelIdentity?: PersonnelIdentity,
    currentUsername?: string
): ILayoutSubMenu | undefined => {
    if (!personnelIdentity) {
        return undefined;
    }

    const isOwnPersonnel =
        !!personnelIdentity.userName &&
        personnelIdentity.userName.toLocaleLowerCase() === currentUsername?.toLocaleLowerCase();

    return {
        code: "personnel",
        title: `${personnelIdentity.firstName} ${personnelIdentity.lastName}`,
        context: {
            personnelId: personnelIdentity.id
        },
        items: [
            {
                icon: undefined,
                label: "Details",
                href: `/personnel/${personnelIdentity.id}/details`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PersonnelView]
            },
            {
                icon: AppSettings.icons.models.sourceDocument,
                label: "Files",
                href: `/personnel/${personnelIdentity.id}/files`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PersonnelFilesView]
            },
            {
                icon: undefined,
                label: "HubSpot",
                href: `/personnel/${personnelIdentity.id}/hubspot`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PersonnelHubspotView]
            },
            {
                icon: AppSettings.icons.models.personnelRole,
                label: "Institutions & Roles",
                href: `/personnel/${personnelIdentity.id}/roles`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PersonnelRoleView]
            },
            {
                icon: AppSettings.icons.models.permission,
                label: "Permissions",
                href: `/personnel/${personnelIdentity.id}/permissions`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PersonnelRoleView]
            },
            {
                icon: AppSettings.icons.models.personnelNote,
                label: "Notes",
                href: `/personnel/${personnelIdentity.id}/notes`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PersonnelNoteView]
            },
            {
                icon: undefined,
                label: "Login & Security",
                href: `/personnel/${personnelIdentity.id}/activedirectory`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PersonnelSecurityView]
            },
            {
                icon: undefined,
                label: "OpenClinica",
                href: `/personnel/${personnelIdentity.id}/openclinica`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.PersonnelOpenClinicaView]
            },
            {
                icon: AppSettings.icons.models.personnelEmailPreference,
                label: "Email Preferences",
                href: `/personnel/${personnelIdentity.id}/emails`,
                permissionMode: LinkPermissionMode.Any,
                permissions: isOwnPersonnel ? undefined : [Permission.PersonnelEmailPreferencesView]
            }
        ]
    };
};

/**
 * Builds group navigation and context, or undefined when the identity is absent.
 */
export const createGroupSubMenu = (groupIdentity?: GroupIdentity): ILayoutSubMenu | undefined => {
    if (!groupIdentity) {
        return undefined;
    }

    return {
        code: "group",
        title: groupIdentity.name,
        context: {
            groupId: groupIdentity.id
        },
        items: [
            {
                icon: undefined,
                label: "Details",
                href: `/groups/${groupIdentity.id}/details`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.GroupView]
            },
            {
                icon: AppSettings.icons.models.personnelRole,
                label: "Personnel",
                href: `/groups/${groupIdentity.id}/personnel`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.GroupPersonnelRoleView]
            },
            {
                icon: AppSettings.icons.models.trial,
                label: "Trials",
                href: `/groups/${groupIdentity.id}/trials`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialView]
            },
            {
                icon: AppSettings.icons.models.groupNote,
                label: "Notes",
                href: `/groups/${groupIdentity.id}/notes`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.GroupNotesView]
            },
            {
                icon: AppSettings.icons.settings,
                label: "Configuration",
                href: `/groups/${groupIdentity.id}/configuration`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.TrialGroupItemTemplatesView]
            }
        ]
    };
};

/**
 * Builds role detail and permission links only when the role ID is truthy.
 *
 * Postconditions: Missing or zero IDs return undefined. Both links retain the system-role update
 * requirement; this helper constructs metadata without performing authorization.
 */
export const createRoleSubMenu = (role?: Role): ILayoutSubMenu | undefined => {
    if (!role?.id) {
        return undefined;
    }

    const basePath = `/settings/general/roles/${role.id}`;

    return {
        code: "role",
        title: role.value,
        items: [
            {
                icon: undefined,
                label: "Details",
                href: `${basePath}/details`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.RolesUpdateSystem]
            },
            {
                icon: AppSettings.icons.models.permission,
                label: "Permissions",
                href: `${basePath}/permissions`,
                permissionMode: LinkPermissionMode.Any,
                permissions: [Permission.RolesUpdateSystem]
            }
        ]
    };
};

export {
    createGeneralSettingsSubMenu,
    createHubspotSettingsSubMenu,
    createInstitutionSettingsSubMenu,
    createParticipantSettingsSubMenu,
    createItemSettingsSubMenu,
    createQaSettingsSubMenu,
    createSettingsSubMenu,
    createTrialSettingsSubMenu
} from "./settingsNavigation";

export { createTrialConfigurationSubMenu } from "./trialConfigurationNavigation";
export { createGroupConfigurationSubMenu } from "./groupConfigurationNavigation";
export { createTrialItemsSubMenu } from "./trialItemsNavigation";
